/**
 * AP-34 13. — **Übergabe an die Buchhaltung**: Auswahl → Exportstapel → herunterladen oder per
 * E-Mail senden → bei Bedarf zurücknehmen; dazu die Exportprofile.
 *
 * Eine Arbeitsseite: „Stapel erzeugen" und „Zurücknehmen" sind Handlungen mit ihrem Knopf.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { MailExportDialog } from '@/components/mail/MailExportDialog'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { frage } from '@/lib/dialoge'
import { downloadFile, extractFilename } from '@/lib/download'
import { exportLaden } from '@/lib/export-request'
import { pageWide } from '@/lib/form-styles'
import { Abschnitt, FeldZeile, SchalterZeile, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { batchFileRequest, createExportBatch, fetchExportBatches, fetchExportProfiles, fetchExportSelection, patchExportProfile, revokeExportBatch } from '../api/incoming-invoices.api'
import { ART_TEXT, fehltText, tagText } from '../lib/texte'
import type { ExportBatch, ExportProfile, ExportSelectionRow } from '../types/incoming-invoices.types'

const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'
const haupt = 'rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50'
const ZIEL: Record<string, string> = { BMD_NTCS_CSV: 'Buchungsstapel für BMD (kontiert)', GENERIC_CSV: 'CSV-Liste', XLSX_LIST: 'Rechnungsliste als Excel', DOCUMENT_ARCHIVE: 'Belegarchiv als ZIP' }
/** Vorgabe des Stichtags: der Monatsletzte des Vormonats (13.3-1). */
const vormonatsletzter = () => {
  const d = new Date()
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 0)).toISOString().slice(0, 10)
}

export default function IncomingHandoverPage() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const darfZurueck = erfuelltRecht('incoming_invoices.export.revoke', user?.permissions)
  const darfProfile = erfuelltRecht('incoming_invoices.settings.manage', user?.permissions)
  const [fehler, setFehler] = useState<string | null>(null)
  const [profile, setProfile] = useState<ExportProfile[]>([])
  const [steuercodes, setSteuercodes] = useState<{ key: string; label: string }[]>([])
  const [stapel, setStapel] = useState<ExportBatch[]>([])
  const [profilId, setProfilId] = useState('')
  const [bis, setBis] = useState(vormonatsletzter())
  const [auswahl, setAuswahl] = useState<ExportSelectionRow[] | null>(null)
  const [wahl, setWahl] = useState<Set<string>>(new Set())
  const [laeuft, setLaeuft] = useState(false)
  const [mailFuer, setMailFuer] = useState<ExportBatch | null>(null)

  const lade = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([fetchExportProfiles(), fetchExportBatches()])
      setProfile(p.profiles)
      setSteuercodes(p.taxCodes)
      setStapel(s)
      setProfilId((alt) => alt || p.profiles.find((x) => x.isDefault)?.id || p.profiles[0]?.id || '')
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Übergabe ließ sich nicht laden.'))
    }
  }, [])
  useEffect(() => {
    void lade()
  }, [lade])

  const tue = async (was: () => Promise<void>, fehlertext: string) => {
    try {
      setLaeuft(true)
      setFehler(null)
      await was()
    } catch (e) {
      setFehler(apiErrorText(e, fehlertext))
    } finally {
      setLaeuft(false)
    }
  }
  const ladeAuswahl = () =>
    tue(async () => {
      const zeilen = await fetchExportSelection(bis, profilId)
      setAuswahl(zeilen)
      // vorausgewählt ist, was vollständig ist
      setWahl(new Set(zeilen.filter((z) => !z.missing.length).map((z) => z.id)))
    }, 'Die Auswahl ließ sich nicht laden.')
  const erzeugen = () =>
    tue(async () => {
      setStapel(await createExportBatch(profilId, [...wahl]))
      setAuswahl(null)
    }, 'Der Stapel ließ sich nicht erzeugen.')
  const laden = (s: ExportBatch) =>
    tue(async () => {
      const r = await exportLaden(batchFileRequest(s.id))
      downloadFile(r.data, extractFilename(r.headers['content-disposition'] as string | undefined, `uebergabe-${s.number}`), String(r.headers['content-type'] ?? 'application/octet-stream'))
    }, 'Die Datei ließ sich nicht laden.')
  const zuruecknehmen = async (s: ExportBatch) => {
    const grund = await frage(`Stapel ${s.number} zurücknehmen? Seine ${s.invoiceCount} Rechnungen sind danach wieder freigegeben und stehen im nächsten Stapel. Wurde die Datei schon in der Buchhaltung eingelesen, dort zuerst löschen. Grund:`, '', { pflicht: true, ok: 'Zurücknehmen' })
    if (grund) await tue(async () => setStapel(await revokeExportBatch(s.id, grund)), 'Der Stapel ließ sich nicht zurücknehmen.')
  }
  const summe = useMemo(() => (auswahl ?? []).filter((z) => wahl.has(z.id)).reduce((s, z) => s + Math.round(z.grossTotal * 100), 0) / 100, [auswahl, wahl])
  const profil = profile.find((p) => p.id === profilId)

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Übergabe an die Buchhaltung</h1>
        <p className="mt-1 text-sm text-muted-foreground">Freigegebene Rechnungen als Stapel übergeben. Ein Stapel entsteht einmal und bleibt unverändert abrufbar; eine übergebene Rechnung lässt sich nicht mehr ändern, nur noch mit einer Stornobuchung korrigieren.</p>
      </div>
      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      <Abschnitt titel="Neuer Stapel">
        <div className="flex flex-wrap items-end gap-3 text-sm">
          <label className="text-xs text-muted-foreground">
            Profil
            <select aria-label="Exportprofil" className={`${feldKlasse} mt-0.5 w-72`} value={profilId} onChange={(e) => (setProfilId(e.target.value), setAuswahl(null))}>
              {profile.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            Rechnungsdatum bis
            <input type="date" aria-label="Rechnungsdatum bis" className={`${feldKlasse} mt-0.5 w-40`} value={bis} onChange={(e) => setBis(e.target.value)} />
          </label>
          <button type="button" className={knopf} disabled={laeuft || !profilId || !bis} onClick={() => void ladeAuswahl()}>
            Auswahl laden
          </button>
          {profil && <span className="pb-1.5 text-xs text-muted-foreground">{ZIEL[profil.target] ?? profil.target}{profil.includeFiles ? ' · mit Belegen' : ''}</span>}
        </div>

        {auswahl && (
          <div className="mt-3 space-y-3 text-sm">
            {auswahl.length === 0 ? (
              <p className="text-muted-foreground">Bis zum {tagText(bis)} ist nichts zu übergeben.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="w-8 px-3 py-1.5" />
                      <th className="px-2 py-1.5 font-normal">Rechnung</th>
                      <th className="px-2 py-1.5 font-normal">Lieferant</th>
                      <th className="px-2 py-1.5 font-normal">Rechnungsnummer</th>
                      <th className="px-2 py-1.5 font-normal">Datum</th>
                      <th className="px-3 py-1.5 text-right font-normal">Brutto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auswahl.map((z) => (
                      <tr key={z.id} className="border-t border-border">
                        <td className="px-3 py-1">
                          <input
                            type="checkbox"
                            aria-label={`${z.number ?? ''} übergeben`}
                            disabled={z.missing.length > 0}
                            checked={wahl.has(z.id)}
                            onChange={(e) => {
                              const neu = new Set(wahl)
                              if (e.target.checked) neu.add(z.id)
                              else neu.delete(z.id)
                              setWahl(neu)
                            }}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Link className="underline decoration-dotted hover:text-primary" to={`/incoming-invoices/${z.id}`}>
                            {z.number}
                          </Link>
                          <span className="ml-2 text-xs text-muted-foreground">{z.reversal ? 'Stornobuchung' : ART_TEXT[z.kind]}</span>
                        </td>
                        <td className="px-2 py-1">{z.supplierName}</td>
                        <td className="px-2 py-1">
                          {z.supplierInvoiceNumber}
                          {z.missing.length > 0 && <span className="block text-xs text-destructive">{fehltText(z)}</span>}
                        </td>
                        <td className="px-2 py-1 tabular-nums">{tagText(z.invoiceDate)}</td>
                        <td className="px-3 py-1 text-right tabular-nums">{geldText(z.grossTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {auswahl.some((z) => z.missing.length > 0) && (
              <p className="text-xs text-muted-foreground">
                Was unvollständig ist, lässt sich nicht ankreuzen. Ein fehlendes Sachkonto trägst du an der Rechnung nach: ein Klick auf ihre Nummer öffnet sie, „Zur Bearbeitung öffnen" macht sie wieder änderbar — die
                Positionen ohne Sachkonto stehen dort in der Prüfung. Die Kreditornummer steht am Lieferanten.
              </p>
            )}
            {auswahl.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" className={haupt} disabled={laeuft || wahl.size === 0} onClick={() => void erzeugen()}>
                  Stapel erzeugen ({wahl.size})
                </button>
                <span className="tabular-nums text-muted-foreground">Summe {geldText(summe)}</span>
              </div>
            )}
          </div>
        )}
      </Abschnitt>

      <Abschnitt titel="Stapel">
        {stapel.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch kein Stapel.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border text-sm">
            {stapel.map((s) => (
              <li key={s.id} className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 ${s.revokedAt ? 'text-muted-foreground' : ''}`}>
                <span className="font-medium tabular-nums">{s.number}</span>
                <span>{s.profileName}</span>
                <span className="text-muted-foreground">{s.invoiceCount} Rechnungen · {tagText(s.periodFrom)} – {tagText(s.periodTo)}</span>
                <span className="text-muted-foreground">erzeugt {tagText(s.createdAt)}</span>
                {s.revokedAt && <span className="rounded bg-muted px-1.5 text-xs">zurückgenommen {tagText(s.revokedAt)}{s.revokeReason ? ` — ${s.revokeReason}` : ''}</span>}
                <span className="ml-auto flex flex-wrap gap-2">
                  {s.hasFile && (
                    <>
                      <button type="button" className={knopf} disabled={laeuft} onClick={() => void laden(s)}>
                        <Download size={14} /> Herunterladen
                      </button>
                      <button type="button" className={knopf} disabled={laeuft} onClick={() => setMailFuer(s)}>
                        <Mail size={14} /> {t('mailExport.menu')}
                      </button>
                    </>
                  )}
                  {darfZurueck && s.revocable && (
                    <button type="button" className={`${knopf} text-destructive`} disabled={laeuft} title="Nur der jüngste Stapel lässt sich zurücknehmen" onClick={() => void zuruecknehmen(s)}>
                      Zurücknehmen
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Abschnitt>

      {darfProfile && profil && profil.target === 'BMD_NTCS_CSV' && <ProfilEinstellungen key={profil.id} profil={profil} steuercodes={steuercodes} onProfile={setProfile} />}

      {/* Jeder Export geht auch per E-Mail: dieselbe Datei — Format und Ausrichtung gibt es hier nicht. */}
      <MailExportDialog
        open={mailFuer !== null}
        onClose={() => setMailFuer(null)}
        source="EXPORT_INCOMING_BATCH"
        title={mailFuer ? `${t('mailExport.titles.EXPORT_INCOMING_BATCH')} ${mailFuer.number}` : ''}
        defaultSubject={mailFuer ? `${t('mailExport.titles.EXPORT_INCOMING_BATCH')} ${mailFuer.number}` : ''}
        request={() => batchFileRequest(mailFuer?.id ?? '')}
        formats={['xlsx']}
        showOrientation={false}
      />
    </div>
  )
}

/**
 * Das BMD-Profil: Steuercodes der Buchhaltung, Kodierung, Belege. Feldweise ohne Knopf (AP-36) —
 * die Tabelle der Steuercodes liegt für die Speicherstelle flach vor (`tax:<Schlüssel>`) und geht
 * als Ganzes zurück.
 */
function ProfilEinstellungen({ profil, steuercodes, onProfile }: { profil: ExportProfile; steuercodes: { key: string; label: string }[]; onProfile: (p: ExportProfile[]) => void }) {
  const flach = (p: ExportProfile): Datensatz => ({ updatedAt: p.updatedAt, encoding: p.encoding, includeFiles: p.includeFiles, ...Object.fromEntries(steuercodes.map((c) => [`tax:${c.key}`, p.taxCodeMap[c.key] ?? ''])) })
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `incoming-export-profile:${profil.id}`,
    bezeichnung: `Exportprofil ${profil.name}`,
    daten: flach(profil),
    schreibe: async (patch) => {
      const { encoding, includeFiles, ...rest } = patch as Record<string, unknown>
      const codes = Object.entries(rest).filter(([k]) => k.startsWith('tax:'))
      const r = await patchExportProfile(profil.id, {
        ...(encoding !== undefined && { encoding }),
        ...(includeFiles !== undefined && { includeFiles }),
        ...(codes.length && { taxCodeMap: { ...profil.taxCodeMap, ...Object.fromEntries(codes.map(([k, v]) => [k.slice(4), String(v ?? '').trim()])) } }),
      })
      onProfile(r.profiles)
      return flach(r.profiles.find((p) => p.id === profil.id) ?? profil)
    },
    werte: (a) => a,
  }) as Speicherstelle<unknown>
  const kodierung = useFeld(stelle, 'encoding', { art: 'auswahl', label: 'Kodierung' })
  const belege = useFeld<boolean>(stelle, 'includeFiles', { art: 'schalter', label: 'Belege mitgeben' })
  return (
    <Abschnitt titel={`Profil „${profil.name}"`}>
      <p className="mb-2 text-sm text-muted-foreground">Welchen Steuercode die Buchhaltung je Steuerart erwartet, entscheidet ihre Einrichtung. Ein leerer Code bleibt in der Datei leer.</p>
      <div className="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
        {steuercodes.map((c) => (
          <SteuercodeZeile key={c.key} stelle={stelle} code={c} />
        ))}
      </div>
      <div className="mt-2 grid gap-x-6 lg:grid-cols-2">
        <FeldZeile label="Kodierung" status={kodierung} hilfe="BMD erwartet Windows-1252">
          <select className={feldKlasse} {...kodierung.props}>
            <option value="windows-1252">Windows-1252</option>
            <option value="utf-8">UTF-8</option>
          </select>
        </FeldZeile>
        <SchalterZeile feld={belege} label="Belege mitgeben" hinweis="Der Stapel wird ein ZIP: Buchungsdatei und die Originale der Rechnungen" />
      </div>
    </Abschnitt>
  )
}

function SteuercodeZeile({ stelle, code }: { stelle: Speicherstelle<unknown>; code: { key: string; label: string } }) {
  const feld = useFeld(stelle, `tax:${code.key}`, { label: code.label })
  return (
    <FeldZeile label={code.label} status={feld}>
      <input aria-label={`Steuercode der Buchhaltung für ${code.label}`} className={feldKlasse} {...feld.props} />
    </FeldZeile>
  )
}
